import { useCallback, useEffect, useRef } from 'react'
import { useRefUpdated } from 'ui/hooks/useRefUpdated'
import Script from 'next/script'
import { Box } from '@chakra-ui/react'
import { type NshiftCheckoutWidgetInstance, type NshiftCheckoutWidgetSettings } from './types'
import './nshift-override.css'

const NSHIFT_WIDGET_URL = 'https://www.nshiftportal.com/checkout/widget'
const SCRIPT_URL = `${NSHIFT_WIDGET_URL}/nshift-checkout-widget.js`
const STYLES_URL = `${NSHIFT_WIDGET_URL}/nshift-checkout-widget.css`

const WIDGET_CONTAINER_ID = 'nshift-checkout-widget-container'

type NshiftCheckoutWidgetComponentProps = Pick<NshiftCheckoutWidgetSettings, 'resultCallback'> & {
  onReady?: (widgetInstance: NshiftCheckoutWidgetInstance) => void
}

export const NshiftCheckoutWidgetComponent = ({
  resultCallback,
  onReady
}: NshiftCheckoutWidgetComponentProps) => {
  const isCheckoutCreated = useRef(false)
  const onReadyRef = useRefUpdated(onReady)
  const resultCallbackRef = useRefUpdated(resultCallback)
  const handleIsReady = useCallback(() => {
    if (isCheckoutCreated.current) {
      return
    }

    const widgetInstance = window.nShiftCheckoutWidget?.createWidget(
      document.getElementById(WIDGET_CONTAINER_ID),
      {
        widgetVersion: '2',
        resultCallback: resultCallbackRef.current
      }
    )
    widgetInstance?.install()

    onReadyRef.current?.(widgetInstance)

    isCheckoutCreated.current = true
  }, [onReadyRef, resultCallbackRef])

  useEffect(() => {
    if (window.nShiftCheckoutWidget) {
      handleIsReady()
    }
  }, [handleIsReady])

  return (
    <>
      <link rel="stylesheet" href={STYLES_URL} />

      <Script src={SCRIPT_URL} onLoad={handleIsReady} />

      <Box
        width="100%"
        sx={{
          '.nshift-checkout-widget .nshift-options-list-legend': {
            display: 'none'
          }
        }}
      >
        <div id={WIDGET_CONTAINER_ID} />
      </Box>
    </>
  )
}
